/**
 * SimulationQuotaService - Gestion des quotas de simulation par tier
 *
 * Modèle (lot #2 « test honnête », décisions 2026-06-18) :
 *   - free  : 0 simulation → la fonction « Tester » est entièrement payante (paywall).
 *   - pro   : 4 / jour ET 20 / mois (les deux gardes s'appliquent).
 * Pas de tier enterprise en production (cf. project_pricing_decision_eur) ; on lui
 * laisse des valeurs larges pour ne pas casser le type UserTier.
 *
 * ⚠️ Quotas client-side (localStorage) : garde-fou UX/coût, pas une sécurité.
 */

import type {
  UserTier,
  SimulationQuota,
  SimulationUsage,
} from "../../types/simulation";

const QUOTAS: Record<UserTier, SimulationQuota> = {
  free: {
    tier: "free",
    simulationsPerMonth: 0,
    simulationsPerDay: 0,
    maxVolume: 10,
    hasGeminiAccess: false,
    hasPdfExport: false,
    canOverrideContext: false,
  },
  pro: {
    tier: "pro",
    simulationsPerMonth: 20,
    simulationsPerDay: 4,
    maxVolume: 50,
    hasGeminiAccess: true,
    hasPdfExport: true,
    canOverrideContext: true,
  },
  enterprise: {
    tier: "enterprise",
    simulationsPerMonth: 1000,
    simulationsPerDay: 1000,
    maxVolume: 100,
    hasGeminiAccess: true,
    hasPdfExport: true,
    canOverrideContext: true,
  },
};

/**
 * Récupère les quotas pour un tier
 */
export function getQuotaForTier(tier: UserTier): SimulationQuota {
  return QUOTAS[tier];
}

/** Début du mois suivant (reset mensuel). */
function nextMonthReset(): Date {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Minuit local du jour suivant (reset journalier). */
function nextDayReset(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Récupère l'usage actuel depuis localStorage, en appliquant les resets
 * mensuel et journalier si leurs dates sont dépassées.
 */
export function getCurrentUsage(tier: UserTier): SimulationUsage {
  const key = `simulation_usage_${tier}`;
  const stored = localStorage.getItem(key);
  const quota = getQuotaForTier(tier);

  if (!stored) {
    return resetUsage(tier);
  }

  let usage: SimulationUsage;
  try {
    const parsed = JSON.parse(stored);
    usage = {
      tier,
      simulationsThisMonth: parsed.simulationsThisMonth ?? 0,
      remainingSimulations:
        parsed.remainingSimulations ?? quota.simulationsPerMonth,
      quotaResetDate: new Date(parsed.quotaResetDate),
      simulationsToday: parsed.simulationsToday ?? 0,
      remainingToday: parsed.remainingToday ?? quota.simulationsPerDay,
      dailyResetDate: new Date(parsed.dailyResetDate ?? nextDayReset()),
    };
  } catch {
    return resetUsage(tier);
  }

  const now = new Date();
  let changed = false;

  // Reset mensuel
  if (usage.quotaResetDate < now) {
    usage.simulationsThisMonth = 0;
    usage.remainingSimulations = quota.simulationsPerMonth;
    usage.quotaResetDate = nextMonthReset();
    changed = true;
  }

  // Reset journalier
  if (usage.dailyResetDate < now) {
    usage.simulationsToday = 0;
    usage.remainingToday = quota.simulationsPerDay;
    usage.dailyResetDate = nextDayReset();
    changed = true;
  }

  if (changed) saveUsage(usage);
  return usage;
}

/**
 * Réinitialise complètement l'usage (mensuel + journalier).
 */
function resetUsage(tier: UserTier): SimulationUsage {
  const quota = getQuotaForTier(tier);
  const usage: SimulationUsage = {
    tier,
    simulationsThisMonth: 0,
    remainingSimulations: quota.simulationsPerMonth,
    quotaResetDate: nextMonthReset(),
    simulationsToday: 0,
    remainingToday: quota.simulationsPerDay,
    dailyResetDate: nextDayReset(),
  };
  saveUsage(usage);
  return usage;
}

/**
 * Sauvegarde l'usage dans localStorage
 */
function saveUsage(usage: SimulationUsage): void {
  const key = `simulation_usage_${usage.tier}`;
  localStorage.setItem(key, JSON.stringify(usage));
}

/**
 * Incrémente les compteurs (mensuel ET journalier) si du quota reste.
 */
export function incrementUsage(tier: UserTier): SimulationUsage {
  const usage = getCurrentUsage(tier);

  if (usage.remainingSimulations > 0 && usage.remainingToday > 0) {
    usage.simulationsThisMonth++;
    usage.remainingSimulations--;
    usage.simulationsToday++;
    usage.remainingToday--;
    saveUsage(usage);
  }

  return usage;
}

/**
 * Vérifie si l'utilisateur peut lancer une simulation (mensuel ET journalier).
 * free (quota 0) → toujours false → déclenche le paywall côté UI.
 */
export function canSimulate(tier: UserTier): boolean {
  const quota = getQuotaForTier(tier);
  if (quota.simulationsPerMonth <= 0 || quota.simulationsPerDay <= 0) {
    return false;
  }
  const usage = getCurrentUsage(tier);
  return usage.remainingSimulations > 0 && usage.remainingToday > 0;
}

/**
 * Récupère les simulations restantes ce mois
 */
export function getRemainingSimulations(tier: UserTier): number {
  return getCurrentUsage(tier).remainingSimulations;
}

/**
 * Récupère les simulations restantes aujourd'hui (cooldown/jour)
 */
export function getRemainingToday(tier: UserTier): number {
  return getCurrentUsage(tier).remainingToday;
}
